import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import PersonIcon from '@mui/icons-material/Person';
import FlightIcon from '@mui/icons-material/Flight';
import PlaceIcon from '@mui/icons-material/Place';
import PhotoIcon from '@mui/icons-material/Photo';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import type { SearchHit, SearchEntityType } from '../types';

function getEntityIcon(type: SearchEntityType) {
  switch (type) {
    case 'user':
      return <PersonIcon color="primary" />;
    case 'trip':
      return <FlightIcon color="secondary" />;
    case 'pin':
      return <PlaceIcon color="info" />;
    case 'photo':
      return <PhotoIcon color="action" />;
    default:
      return null;
  }
}

function getEntityColor(type: SearchEntityType): 'primary' | 'secondary' | 'info' | 'default' {
  switch (type) {
    case 'user':
      return 'primary';
    case 'trip':
      return 'secondary';
    case 'pin':
      return 'info';
    default:
      return 'default';
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface SearchResultCardProps {
  hit: SearchHit;
  onClick?: (hit: SearchHit) => void;
}

/** Renders a user search result card */
function UserResultCard({ hit, onClick }: SearchResultCardProps) {
  return (
    <Card
      sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': { boxShadow: 3 } }}
      onClick={() => onClick?.(hit)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {getEntityIcon('user')}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {hit.title}
            </Typography>
            {hit.country_name && (
              <Typography variant="body2" color="text.secondary">
                {hit.country_name}
              </Typography>
            )}
          </Box>
          <Chip label="User" size="small" color="primary" variant="outlined" />
        </Box>
        {hit.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
            {hit.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/** Renders a trip search result card */
function TripResultCard({ hit, onClick }: SearchResultCardProps) {
  return (
    <Card
      sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': { boxShadow: 3 } }}
      onClick={() => onClick?.(hit)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {getEntityIcon('trip')}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {hit.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hit.city && (
                <Typography variant="body2" color="text.secondary">
                  {hit.city}{hit.country_name ? `, ${hit.country_name}` : ''}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Chip label="Trip" size="small" color="secondary" variant="outlined" />
            {hit.category && (
              <Chip label={hit.category} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
            )}
          </Box>
        </Box>
        {hit.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
            {hit.description}
          </Typography>
        )}
        {(hit.start_date || hit.created_at) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">
              {hit.start_date ? formatDate(hit.start_date) : formatDate(hit.created_at)}
              {hit.end_date && ` - ${formatDate(hit.end_date)}`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/** Renders a pin search result card */
function PinResultCard({ hit, onClick }: SearchResultCardProps) {
  return (
    <Card
      sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': { boxShadow: 3 } }}
      onClick={() => onClick?.(hit)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {getEntityIcon('pin')}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {hit.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hit.city && `${hit.city}, `}{hit.country_name || hit.country_code || ''}
            </Typography>
          </Box>
          <Chip label="Pin" size="small" color="info" variant="outlined" />
        </Box>
        {hit.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
            {hit.description}
          </Typography>
        )}
        {hit.visit_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">
              Visited: {formatDate(hit.visit_date)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Renders the appropriate search result card based on entity type.
 */
export function SearchResultCard({ hit, onClick }: SearchResultCardProps) {
  switch (hit.type) {
    case 'user':
      return <UserResultCard hit={hit} onClick={onClick} />;
    case 'trip':
      return <TripResultCard hit={hit} onClick={onClick} />;
    case 'pin':
      return <PinResultCard hit={hit} onClick={onClick} />;
    case 'photo':
      return <PinResultCard hit={hit} onClick={onClick} />;
    default:
      return (
        <Card
          sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': { boxShadow: 3 } }}
          onClick={() => onClick?.(hit)}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">{hit.title}</Typography>
                {hit.description && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {hit.description}
                  </Typography>
                )}
              </Box>
              <Chip label={hit.type} size="small" color={getEntityColor(hit.type)} variant="outlined" />
            </Box>
          </CardContent>
        </Card>
      );
  }
}
